import {
  CheckpointIProps,
  DeviceI,
  FloorFacilityType,
  FloorI,
  FloorType,
  TourPlanCheckpointsType,
  TourPlanI,
  TourPlansType,
} from '../types/types'

/**
 * Sorts floors by displayOrder (ascending), falling back to alphabetical name
 * sorting for floors without a display order. Floors with displayOrder appear
 * before those without.
 *
 * @param f1 - First floor to compare
 * @param f2 - Second floor to compare
 * @returns Negative if f1 should come first, positive if f2 should come first
 */
export const sortFloorsByDisplayOrderThenName = (
  f1: { displayOrder?: number | null; name?: string | null },
  f2: { displayOrder?: number | null; name?: string | null }
) => {
  // If both have displayOrder, sort by displayOrder
  if (f1.displayOrder != null && f2.displayOrder != null) {
    return f1.displayOrder - f2.displayOrder
  }
  // If only f1 has displayOrder, it comes first
  if (f1.displayOrder != null) return -1
  // If only f2 has displayOrder, it comes first
  if (f2.displayOrder != null) return 1
  // If neither has displayOrder, sort alphabetically by name
  return (f1.name ?? '').localeCompare(f2.name ?? '')
}

interface FloorIProps {
  id: string
  name?: string
  incidents?: { totalCount?: number }
}

export const mapFloor = (
  floor: FloorIProps,
  defaultFloorId: string
): FloorI => {
  return {
    id: floor.id,
    label: floor.name,
    hasIncidents: floor.incidents?.totalCount > 0,
    isDefaultFloor: floor.id === defaultFloorId,
  }
}

export const mapCheckpointsData = (
  devices: DeviceI[]
): Array<CheckpointIProps> => {
  return devices?.map((d) => {
    return {
      id: d?.id,
      name: d?.deviceName,
      displayId: d?.checkpoint?.displayId,
      isPhotoRequired: d?.checkpoint?.isPhotoRequired,
      isPlaced: d.placed,
      x: d?.x,
      y: d?.y,
      facility: d?.checkpoint?.facility,
      floor: d?.checkpoint?.floor,
      createdAt: d?.createdAt,
    }
  })
}

export const mapTourPlanCheckpoints = (
  tourPlan: TourPlanCheckpointsType
): Array<CheckpointIProps> => {
  return tourPlan?.checkpoints?.edges?.map((d) => {
    return {
      id: d.node?.id,
      name: d.node?.device?.name,
      displayId: d.node?.displayId,
      isPlaced: Boolean(d.node?.device?.x && d.node?.device?.y),
      isPhotoRequired: d.node?.isPhotoRequired,
      x: d.node?.device?.x,
      y: d.node?.device?.y,
      facility: d.node?.device?.facility as FloorFacilityType,
      floor: d.node?.device?.floor as FloorType,
      createdAt: d.node?.device?.createdAt,
    }
  })
}

export const mapTourPlans = (
  facilityTourPlans: TourPlansType
): Array<TourPlanI> => {
  return facilityTourPlans?.edges?.map((t) => ({
    id: t?.node?.id,
    name: t?.node?.name,
    checkpointCount: t?.node?.checkpointIds?.length,
    checkpointIds: t?.node?.checkpointIds,
  }))
}
